import React, { useEffect, useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardBody,
  Col,
  Container,
  Row,
  Table,
  Modal,
  ModalHeader,
  ModalBody,
  Label,
  Input,
  ModalFooter,
} from "reactstrap";
import Skeleton from "react-loading-skeleton";
import { useDispatch, useSelector } from "react-redux";
import { createSelector } from "reselect";
import NF from "../../../assets/images/file.png";
import ConfirmModal from "../../../Components/Common/ConfirmModal";
import CustomPagination from "../../../Components/Common/CustomePagination";
import {
  addInvioceCharges,
  deleteInvoiceCharges,
  editInvoiceCharges,
  fetchBillChargesInvoicePDF,
  fetchInvoiceChargesList,
  updateChargesNote,
  updateInvoiceChargesStatusThunk,
} from "../../../slices/InvoiceCharges/thunk";

import GenerateExtraChargesBillForm from "../../Forms/Transaction/ExtraChargesBillForm";
import ViewExtraChargesModal from "../../../Modals/InvoiceCharges/ViewExtraChargeBillModal";
import EditExtraChargeBill from "../../Forms/Transaction/EditExtraChargeBill";
import { CheckCircle, XCircle } from "react-feather";
import Swal from "sweetalert2";

const InvoiceChargesList = () => {
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [debounceSearch, setDebounceSearch] = useState("");

  const [pageNo, setPageNo] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showBillModal, setShowBillModal] = useState(false);
  const [selectedCharge, setSelectedCharge] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editBillId, setEditBillId] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [showExtraModal, setShowExtraModal] = useState(false);
  const [selectedExtraBill, setSelectedExtraBill] = useState(null);

  const invoiceChargesSelector = createSelector(
    (state) => state,
    (state) => ({
      invoiceCharges: state.InvoiceCharges.invoiceCharges,
      loading: state.InvoiceCharges.loading,
      error: state.InvoiceCharges.error,
      pagination: state.InvoiceCharges.pagination,
      activeSessions: state.Session.selectedSessionYear,
    }),
  );

  const { invoiceCharges, loading, error, activeSessions, pagination } =
    useSelector(invoiceChargesSelector);

  // Toggle the modal visibility
  const toggleModal = () => setIsModalOpen(!isModalOpen);

  const handleViewExtraBill = (bill) => {
    setSelectedExtraBill(bill);
    setShowExtraModal(true);
  };

  const handleOpenAdd = () => {
    setShowAddForm(true);
  };

  const handleUpdateInvoice = async (id, udpdatedBill) => {
    await dispatch(editInvoiceCharges(id, udpdatedBill));
    resetSearch();
    loadInvoiceCharges();
  };

  const handleAddInvoice = async (invoiceData) => {
    await dispatch(addInvioceCharges(invoiceData));
    resetSearch();
    await loadInvoiceCharges();
    setShowAddForm(false);
  };

  const handleEditBill = (billId) => {
    setShowEditForm(true);
    setEditBillId(billId);
  };

  // Load invoice charges
  const loadInvoiceCharges = async () => {
    await dispatch(
      fetchInvoiceChargesList({
        pageNo,
        pageSize,
        searchTerm: debounceSearch.trim(),
        sessionId: activeSessions?.id,
      }),
    );
  };

  useEffect(() => {
    const t = setTimeout(() => setDebounceSearch(searchTerm), 500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    // loadInvoiceCharges();
    dispatch(
      fetchInvoiceChargesList({
        pageNo,
        pageSize,
        searchTerm: debounceSearch.trim(),
        sessionId: activeSessions?.id,
      }),
    );
  }, [pageNo, pageSize, debounceSearch, activeSessions]);

  const handleDelete = (id) => {
    setDeleteId(id);
    setIsConfirmOpen(true);
  };

  const confirmDelete = async () => {
    await dispatch(deleteInvoiceCharges(deleteId));
    resetSearch();
    loadInvoiceCharges();
  };

  const handleDownloadPdf = async (id) => {
    const result = await Swal.fire({
      icon: "question",
      title: "Download Bill",
      text: "Do you want to download this bill?",
      showCancelButton: true,
      confirmButtonText: "Yes, Download",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#6c757d",
    });

    if (result.isConfirmed) {
      await dispatch(fetchBillChargesInvoicePDF(id));
    }
  };

  const handleToggleStatus = async (charge) => {
    const id = charge.id;
    const active = !charge.active;

    // dispatch or API call
    await dispatch(updateInvoiceChargesStatusThunk(id, active));
    resetSearch();
    await loadInvoiceCharges();
  };

  const resetSearch = () => {
    setSearchTerm("");
    setDebounceSearch("");
    setPageNo(0);
  };

  return (
    <div className="page-content">
      <Container fluid>
        <Row>
          <Col lg={12}>
            <Card>
              <CardHeader>
                <div className="d-flex align-items-center justify-content-between flex-wrap">
                  <h4 className="card-title mb-0">
                    {showAddForm
                      ? "Generate Bill "
                      : "Sales Invoice (Extra Charges)"}
                  </h4>

                  {/* Conditional Buttons */}
                  {!showAddForm && !showEditForm ? (
                    <Button
                      color="primary"
                      className="btn-sm"
                      onClick={handleOpenAdd}
                    >
                      <i className="ri-add-line me-1" />
                      Generate Bill
                    </Button>
                  ) : showEditForm ? (
                    <Button
                      color="secondary"
                      className="btn-sm"
                      onClick={() => {
                        setShowEditForm(false);
                        setEditBillId(null);
                      }}
                    >
                      <i className="ri-arrow-left-line me-1" />
                      Back to Listing
                    </Button>
                  ) : (
                    <Button
                      color="secondary"
                      className="btn-sm"
                      onClick={() => setShowAddForm(false)}
                    >
                      <i className="ri-arrow-left-line me-1" />
                      Back to Listing
                    </Button>
                  )}
                </div>
              </CardHeader>

              <CardBody>
                {showAddForm ? (
                  <GenerateExtraChargesBillForm
                    onSubmit={handleAddInvoice}
                    onCloseForm={() => setShowAddForm(false)}
                  />
                ) : showEditForm ? (
                  <EditExtraChargeBill
                    billId={editBillId}
                    onSubmit={handleUpdateInvoice}
                    onCloseForm={() => {
                      setShowEditForm(false);
                      setEditBillId(null);
                    }}
                  />
                ) : (
                  <>
                    {/* Search Bar */}
                    <div className="d-flex justify-content-between mb-3">
                      <div className="search-box w-25">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search invoice charges..."
                          value={searchTerm}
                          onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPageNo(0);
                          }}
                        />
                      </div>
                    </div>

                    {/* Table */}
                    <div className="table-responsive">
                      <Table className="table-nowrap align-middle">
                        <thead className="table-light">
                          <tr>
                            <th>Sr No</th>
                            <th>Invoice No </th>
                            <th>Date</th>
                            <th>Customer Name</th>

                            <th>Vehicle No</th>
                            <th>Toll Charges</th>
                            <th>Other Charges</th>
                            <th>Handling Charges</th>
                            <th>Total Amount</th>
                            <th>Payment Done</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {loading ? (
                            [...Array(pageSize)].map((_, i) => (
                              <tr key={i}>
                                {[...Array(8)].map((__, j) => (
                                  <td key={j}>
                                    <Skeleton height={15} width={80} />
                                  </td>
                                ))}
                              </tr>
                            ))
                          ) : invoiceCharges?.length === 0 ? (
                            <tr>
                              <td
                                colSpan="8"
                                className="text-center text-muted"
                              >
                                <img
                                  src={NF}
                                  alt="No File"
                                  style={{ width: "500px", marginTop: "10px" }}
                                />
                              </td>
                            </tr>
                          ) : (
                            invoiceCharges?.map((charge, index) => (
                              <tr
                                key={charge?.id}
                                onClick={() => handleViewExtraBill(charge)}
                                style={{ cursor: "pointer" }}
                              >
                                <td>{pageNo * pageSize + index + 1}</td>
                                <td>{charge?.invoiceNo}</td>
                                <td>{charge?.billDate}</td>
                                <td>{charge?.customerName}</td>

                                {/* <td>
                                  {charge?.chargesInvoiceResponses?.[0]
                                    ?.vehicleNo || "-"}
                                </td> */}

                                <td>
                                  {[
                                    ...new Set(
                                      charge?.chargesInvoiceResponses?.map(
                                        (item) => item?.vehicleNo,
                                      ),
                                    ),
                                  ]
                                    .filter(Boolean)
                                    .join(", ") || "-"}
                                </td>
                                <td>{charge?.tollCharges}</td>
                                <td>{charge?.otherCharges}</td>
                                <td>{charge?.handlingCharges}</td>
                                <td>{charge?.totalAmount}</td>

                                <td
                                  style={{
                                    cursor: "pointer",
                                    textAlign: "center",
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStatus(charge);
                                  }}
                                >
                                  {charge.active ? (
                                    <CheckCircle
                                      size={20}
                                      color="#16a34a"
                                      title="Active - Click to deactivate"
                                    />
                                  ) : (
                                    <XCircle
                                      size={20}
                                      color="#dc2626"
                                      title="Inactive - Click to activate"
                                    />
                                  )}
                                </td>

                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-success btn-icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleEditBill(charge?.id);
                                      }}
                                    >
                                      <i className="ri-edit-2-line"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger btn-icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDelete(charge?.id);
                                      }}
                                    >
                                      <i className="ri-delete-bin-line"></i>
                                    </button>

                                    <button
                                      className="btn btn-sm btn-outline-primary btn-icon"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadPdf(charge?.id);
                                      }}
                                    >
                                      <i class="ri-printer-line"></i>
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    </div>

                    {/* Pagination */}
                    <CustomPagination
                      currentPage={pagination?.currentPage}
                      totalPages={pagination?.totalPages}
                      onPageChange={(page) => setPageNo(page)}
                      pageSize={pageSize}
                      setPageSize={setPageSize}
                      setPageNo={setPageNo}
                    />
                  </>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <ConfirmModal
        isOpen={isConfirmOpen}
        toggle={() => setIsConfirmOpen(!isConfirmOpen)}
        title="Delete Invoice Charge"
        message="Are you sure you want to delete this charge?"
        onConfirm={confirmDelete}
      />
      <ViewExtraChargesModal
        show={showExtraModal}
        toggle={() => setShowExtraModal(false)}
        bill={selectedExtraBill}
      />
    </div>
  );
};

export default InvoiceChargesList;
